import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { Row, Col, Card, Button, Alert, ButtonGroup, Accordion, Form } from 'react-bootstrap';
import { Document as PDFDocument, Page as PDFPage, pdfjs } from 'react-pdf';
import { formatCurrency } from '../../utils/formatters';
import { fetchFileContent, updateStatementData, deleteStatement, fetchProcessedBankStatements, fetchProcessedCCStatements, updateFileMetadata, fetchGlEntries, fetchFinancialStatements, fetchFileEntityMentions, fetchInvoiceLineItems, updateInvoiceLineItem, InvoiceLineItemUpdate } from '../../api/cases';
import { useAuth } from '../../contexts/AuthContext';
import { FileEntityMentionsResponse, CaseEntity } from '../../types';
import { formatTimestamp, formatDate } from '../../utils/dateFormatters';
import 'bootstrap-icons/font/bootstrap-icons.css';
import { ZoomIn, ZoomOut, PencilSquare, Plus } from 'react-bootstrap-icons';
import StatementEditor from '../../components/statement/StatementEditor';
import CheckImagesView from '../../components/cases/CheckImagesView';
import { denormalizeFileType, normalizeFileType } from '../../utils/fileTypeUtils';

// Set worker - use CDN for reliability
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// Import additional styles for text layer
import 'react-pdf/dist/esm/Page/TextLayer.css';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';

interface Transaction {
  transaction_id: string;
  date: string;
  description: string;
  amount: number;
  transaction_type: 'credit' | 'debit';
}

interface CCTransaction {
  transaction_id: string;
  date: string;
  description: string;
  amount: number;
  transaction_type: 'charge' | 'payment' | 'credit' | 'fee';
}

interface CCStatement {
  statement_id: string;
  card_issuer: string;
  card_type: string;
  account_number: string;
  account_owner: string;
  statement_date: string;
  previous_balance: number;
  new_balance: number;
  credit_limit?: number;
  minimum_payment_due?: number;
  payment_due_date?: string;
  file_id: string;
  page_number?: number;
  page_numbers?: number[];
  transactions: CCTransaction[];
}

interface Statement {
  statement_id: string;
  bank_name: string;
  account_number: string;
  statement_date: string;
  beginning_balance: number;
  ending_balance: number;
  file_id: string;
  transactions: Transaction[];
  page_number?: number;
  page_numbers?: number[];
}

// Error boundary component for PDF rendering
class PDFErrorBoundary extends React.Component<
  { children: React.ReactNode; onError: (error: Error) => void },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode; onError: (error: Error) => void }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('PDF Error:', error, errorInfo);
    this.props.onError(error);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Alert variant="warning">
          <Alert.Heading>PDF Display Error</Alert.Heading>
          <p>
            Unable to display this PDF file. This may be due to the file being too large or corrupted.
          </p>
          <hr />
          <div className="d-flex justify-content-between align-items-center">
            <Button 
              variant="primary" 
              size="sm" 
              onClick={() => window.open(this.props.children as any, '_blank')}
            >
              Download Instead
            </Button>
            <Button 
              variant="outline-secondary" 
              size="sm" 
              onClick={() => this.setState({ hasError: false, error: null })}
            >
              Try Again
            </Button>
          </div>
        </Alert>
      );
    }

    return this.props.children;
  }
}

interface PageClassification {
  page_number: number;
  primary_type: string;
  file_types: string[];
  confidence: 'high' | 'medium' | 'low';
  notes?: string;
}

interface FileData {
  file: {
    filename: string;
    file_id: string;
    file_type: string;
    mime_type: string;
    upload_date: string;
    uploaded_by: string;
    is_multi_type?: boolean;
    page_count?: number;
    page_classifications?: PageClassification[];
    status?: string;
    processing_status?: string;
    bank_processing_status?: string;
    gl_processing_status?: string;
    /** page number (as string, matching the API) -> degrees clockwise to rotate
     * for upright display. Auto-detected for invoices from Document
     * Intelligence's read angle; manually correctable in the viewer for any
     * file type. */
    page_rotations?: Record<string, number>;
  };
  view_url: string;
  preview_url?: string;
  statement_data?: Statement[];
  gl_entries?: GlEntry[];
  cc_statement_data?: CCStatement[];
  financial_statements?: FinancialStatement[];
  invoice_line_items?: InvoiceLineItem[];
}

interface InvoiceLineItem {
  line_item_id: string;
  page_number: number;
  entry_date?: string;
  vendor?: string;
  invoice_number?: string;
  description?: string;
  amount: number;
  is_duplicate?: boolean;
  duplicate_of?: string;
  amount_verified?: boolean;
  needs_review?: boolean;
  review_reasons?: string[];
  review_details?: Record<string, string>;
  duplicate_of_summary?: {
    line_item_id: string;
    file_id: string;
    filename: string;
    page_number: number;
    vendor: string;
    entry_date: string;
    amount: string;
    same_file: boolean;
    same_page: boolean;
  };
  amount_ocr_confidence?: number;
  extraction_confidence?: 'high' | 'medium' | 'low';
  reviewed?: boolean;
  reviewed_by?: string;
  reviewed_at?: string;
}

interface FinancialStatementLineItem {
  label: string;
  amount: number | string;
}

interface FinancialStatement {
  statement_id: string;
  statement_type: 'balance_sheet' | 'income_statement' | 'investment_statement' | 'trial_balance' | 'other';
  entity_name: string;
  institution_name?: string;
  account_number?: string;
  period_end_date: string;
  period_start_date?: string;
  // Numeric values arrive as strings (backend serializes Decimal via str()).
  totals?: Record<string, number | string>;
  line_items: FinancialStatementLineItem[];
  page_numbers?: (number | string)[];
  original_filename?: string;
}

interface GlEntry {
  entry_id: string;
  account_code: string;
  account_name: string;
  account_type: string;
  entry_date: string;
  transaction_type?: string;
  name?: string;
  memo?: string;
  description?: string;
  split_account?: string;
  cleared_status?: string;
  debit_amount?: number;
  credit_amount?: number;
  net_amount?: number;
  reference_number?: string;
}

// Derive all unique detected file types from page_classifications, ordered by first appearance.
// Returns an array of { type, pages } where pages is the sorted list of 1-indexed page numbers.
interface DetectedType {
  type: string;       // normalized snake_case
  pages: number[];    // sorted page numbers that have this as primary_type
}

function getDetectedTypes(fileData: FileData): DetectedType[] {
  const classifications = fileData.file.page_classifications;
  if (!classifications || classifications.length === 0) {
    // Fall back to the single file_type
    return [{ type: fileData.file.file_type, pages: [] }];
  }

  const typeMap = new Map<string, number[]>();
  for (const pc of classifications) {
    const t = pc.primary_type;
    if (!typeMap.has(t)) typeMap.set(t, []);
    typeMap.get(t)!.push(pc.page_number);
  }

  // Sort pages within each type
  const result: DetectedType[] = [];
  for (const [type, pages] of typeMap.entries()) {
    result.push({ type, pages: pages.sort((a, b) => a - b) });
  }
  return result;
}

// Format a sorted array of page numbers into a compact string, e.g. [1,2,3,6] → "1–3, 6"
function formatPageRanges(pages: number[]): string {
  if (pages.length === 0) return '';
  const ranges: string[] = [];
  let start = pages[0];
  let end = pages[0];
  for (let i = 1; i < pages.length; i++) {
    if (pages[i] === end + 1) {
      end = pages[i];
    } else {
      ranges.push(start === end ? `${start}` : `${start}–${end}`);
      start = pages[i];
      end = pages[i];
    }
  }
  ranges.push(start === end ? `${start}` : `${start}–${end}`);
  return `p. ${ranges.join(', ')}`;
}

// Helper function to detect mime type from filename
const getMimeTypeFromFilename = (filename: string): string => {
  const ext = filename.toLowerCase().split('.').pop();
  const mimeTypes: Record<string, string> = {
    'pdf': 'application/pdf',
    'png': 'image/png',
    'jpg': 'image/jpeg',
    'jpeg': 'image/jpeg',
    'gif': 'image/gif',
    'webp': 'image/webp',
    'bmp': 'image/bmp',
    'tiff': 'image/tiff',
    'tif': 'image/tiff',
  };
  return mimeTypes[ext || ''] || 'application/octet-stream';
};

// File type options for the dropdown (UI display names)
const FILE_TYPE_OPTIONS = [
  'Bank Statements',
  'Bank credit (deposit)',
  'Bank debit (withdrawal)',
  'Bank signature card',
  'Check Images',
  'Check register',
  'Deposit image',
  'Financial statement - Balance sheet',
  'Financial statement - Income statement',
  'Financial statement - Statement of cash flows',
  'Financial statement - Other',
  'IRS Tax form 1040',
  'IRS Tax form 1065',
  'IRS Tax form 1099',
  'IRS Tax form 1120',
  'IRS Tax form 1120S',
  'IRS Tax form 941',
  'IRS Tax form K-1',
  'IRS Tax form Other',
  'IRS Tax form W2',
  'IRS Tax form W4',
  'Appraisal',
  'Correspondence',
  'Credit card statement',
  'Deeds and titles',
  'Depreciation schedule',
  'Email',
  'Expense report',
  'General ledger',
  'Investment account statement',
  'Invoice',
  'Lease agreement',
  'Legal document',
  'Loan application',
  'Loan statement',
  'Note payable',
  'Trial balance',
  'Wire transfer',
  'Other',
].sort();

// ─── GeneralLedgerPanel ─────────────────────────────────────────────────────

interface GeneralLedgerPanelProps {
  isGeneralLedgerType: boolean;
  glEntries: GlEntry[];
  multiType: boolean;
  processingStatus?: string;
}

const GeneralLedgerPanel: React.FC<GeneralLedgerPanelProps> = ({
  isGeneralLedgerType, glEntries, multiType, processingStatus,
}) => {
  if (!isGeneralLedgerType) return null;

  // The global "Processing Banner" above the tabs already shows a spinner +
  // message while processingStatus === 'processing' — don't duplicate it here.
  if (processingStatus === 'processing') return null;

  if (processingStatus === 'error') {
    return (
      <div className={multiType ? '' : 'mt-4'}>
        {!multiType && <h5 className="mb-3">General Ledger</h5>}
        <Alert variant="danger">Failed to extract general ledger entries. Please re-upload the file or contact support.</Alert>
      </div>
    );
  }

  if (glEntries.length === 0) {
    if (!processingStatus) return null;
    return (
      <div className={multiType ? '' : 'mt-4'}>
        {!multiType && <h5 className="mb-3">General Ledger</h5>}
        <Alert variant="secondary">No general ledger entries were extracted from this file.</Alert>
      </div>
    );
  }

  // Group entries by account
  const accountMap = new Map<string, { name: string; type: string; entries: GlEntry[] }>();
  for (const entry of glEntries) {
    const key = entry.account_code || entry.account_name || 'Uncategorized';
    if (!accountMap.has(key)) {
      accountMap.set(key, { name: entry.account_name, type: entry.account_type, entries: [] });
    }
    accountMap.get(key)!.entries.push(entry);
  }
  const accounts = Array.from(accountMap.entries()).sort(([a], [b]) => a.localeCompare(b));

  const typeColor: Record<string, string> = {
    asset: 'success', liability: 'danger', equity: 'primary',
    revenue: 'info', expense: 'warning',
  };

  return (
    <div className={multiType ? '' : 'mt-4'}>
      {!multiType && <h5 className="mb-3">General Ledger</h5>}
      <Accordion alwaysOpen>
        {accounts.map(([code, { name, type, entries }], idx) => {
          const totalDebit = entries.reduce((s, e) => s + (e.debit_amount ?? 0), 0);
          const totalCredit = entries.reduce((s, e) => s + (e.credit_amount ?? 0), 0);
          const colorKey = type?.toLowerCase();
          const badgeVariant = typeColor[colorKey] ?? 'secondary';
          return (
            <Accordion.Item key={code} eventKey={String(idx)}>
              <Accordion.Header>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <strong>{code}</strong>
                  <span className="text-muted">{name}</span>
                  <span className={`badge bg-${badgeVariant} fw-normal`}>{type}</span>
                  <span className="badge bg-secondary fw-normal">{entries.length} entries</span>
                </div>
              </Accordion.Header>
              <Accordion.Body className="p-0">
                <div className="table-responsive">
                  <table className="table table-sm table-hover mb-0">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: '88px' }}>Date</th>
                        <th style={{ width: '100px' }}>Type</th>
                        <th style={{ width: '130px' }}>Name</th>
                        <th>Memo / Description</th>
                        <th style={{ width: '80px' }}>Ref #</th>
                        <th style={{ width: '120px' }}>Split</th>
                        <th style={{ width: '24px' }} title="Cleared">Clr</th>
                        <th className="text-end" style={{ width: '100px' }}>Debit</th>
                        <th className="text-end" style={{ width: '100px' }}>Credit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entries.map(e => (
                        <tr key={e.entry_id}>
                          <td className="text-nowrap small">{e.entry_date}</td>
                          <td className="small text-muted">{e.transaction_type ?? ''}</td>
                          <td className="small">{e.name ?? ''}</td>
                          <td className="small">{e.memo || e.description || ''}</td>
                          <td className="small text-muted">{e.reference_number ?? ''}</td>
                          <td className="small text-muted fst-italic">{e.split_account ?? ''}</td>
                          <td className="small text-center">
                            {e.cleared_status === 'Reconciled' ? 'R'
                              : e.cleared_status === 'Cleared' ? 'C'
                              : ''}
                          </td>
                          <td className="text-end small">{e.debit_amount ? formatCurrency(e.debit_amount) : ''}</td>
                          <td className="text-end small">{e.credit_amount ? formatCurrency(e.credit_amount) : ''}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="table-light fw-semibold">
                      <tr>
                        <td colSpan={7} className="small">Totals</td>
                        <td className="text-end small">{totalDebit !== 0 ? formatCurrency(totalDebit) : ''}</td>
                        <td className="text-end small">{totalCredit !== 0 ? formatCurrency(totalCredit) : ''}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </Accordion.Body>
            </Accordion.Item>
          );
        })}
      </Accordion>
    </div>
  );
};

// ─── InvoiceLineItemsPanel ──────────────────────────────────────────────────

interface InvoiceLineItemsPanelProps {
  isInvoiceType: boolean;
  lineItems: InvoiceLineItem[];
  multiType: boolean;
  processingStatus?: string;
  onGoToPage: (page: number) => void;
  case_id: string;
  file_id: string;
}

// Confidence problems (the read itself may be wrong) are red; duplicates are a
// different kind of question - "is this a repeat?" - so they're amber.
const REVIEW_REASONS: Record<string, { label: string; variant: 'danger' | 'warning' }> = {
  low_ocr_confidence: { label: 'Low OCR confidence', variant: 'danger' },
  model_unsure: { label: 'AI unsure', variant: 'danger' },
  amount_not_verified: { label: 'Amount not in OCR text', variant: 'danger' },
  amount_not_on_line: { label: 'Amount not read by OCR', variant: 'danger' },
  larger_amount_on_page: { label: 'Bigger amount on page', variant: 'danger' },
  date_out_of_range: { label: 'Date looks wrong', variant: 'danger' },
  missing_date: { label: 'Date unreadable', variant: 'danger' },
  missing_vendor: { label: 'Vendor unreadable', variant: 'danger' },
  illegible_description: { label: 'Marked illegible', variant: 'danger' },
  zero_amount: { label: 'Zero amount', variant: 'danger' },
  duplicate_same_page: { label: 'Repeated on this page', variant: 'warning' },
  possible_duplicate: { label: 'Possible duplicate', variant: 'warning' },
};
// Mirrors LOW_OCR_CONFIDENCE_THRESHOLD in processInvoiceUpload.py - only used
// here to color the confidence column, not to decide what's flagged (that's
// already baked into needs_review/review_reasons by the backend).
const LOW_OCR_CONFIDENCE_THRESHOLD = 0.7;
const reasonLabel = (r: string) => REVIEW_REASONS[r]?.label ?? r;
const reasonVariant = (r: string) => REVIEW_REASONS[r]?.variant ?? 'danger';

type ReviewFilter = 'all' | 'needs_review' | 'reviewed' | string;

interface InvoiceLineItemDraft {
  vendor: string;
  entry_date: string;
  description: string;
  invoice_number: string;
  amount: string;
}

// The PUT endpoint serializes DynamoDB numbers as strings (unlike the GET, which
// returns real numbers), so coerce before merging or totals turn into string concatenation.
const normalizeInvoiceLineItem = (raw: any): InvoiceLineItem => ({
  ...raw,
  amount: Number(raw.amount),
  page_number: Number(raw.page_number),
  ...(raw.amount_ocr_confidence != null ? { amount_ocr_confidence: Number(raw.amount_ocr_confidence) } : {}),
});

const InvoiceLineItemsPanel: React.FC<InvoiceLineItemsPanelProps> = ({
  isInvoiceType, lineItems: initialLineItems, multiType, processingStatus, onGoToPage, case_id, file_id,
}) => {
  const auth = useAuth();
  const [lineItems, setLineItems] = useState<InvoiceLineItem[]>(initialLineItems);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<InvoiceLineItemDraft | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [filter, setFilter] = useState<ReviewFilter>('all');

  useEffect(() => {
    setLineItems(initialLineItems);
  }, [initialLineItems]);

  if (!isInvoiceType) return null;

  if (processingStatus === 'processing') return null;

  if (processingStatus === 'error') {
    return (
      <div className={multiType ? '' : 'mt-4'}>
        {!multiType && <h5 className="mb-3">Invoice Line Items</h5>}
        <Alert variant="danger">Failed to extract invoice line items. Please re-upload the file or contact support.</Alert>
      </div>
    );
  }

  if (lineItems.length === 0) {
    if (!processingStatus) return null;
    return (
      <div className={multiType ? '' : 'mt-4'}>
        {!multiType && <h5 className="mb-3">Invoice Line Items</h5>}
        <Alert variant="secondary">No invoice line items were extracted from this file.</Alert>
      </div>
    );
  }

  const total = lineItems.reduce((s, i) => s + (i.amount || 0), 0);
  const needsReviewCount = lineItems.filter(i => i.needs_review).length;
  const reviewedCount = lineItems.filter(i => i.reviewed).length;

  // Rows still needing review, counted per reason. A row with two reasons counts under both.
  const reasonCounts: Record<string, number> = {};
  lineItems.filter(i => i.needs_review).forEach(i =>
    (i.review_reasons || []).forEach(r => { reasonCounts[r] = (reasonCounts[r] || 0) + 1; })
  );
  const reasonKeys = Object.keys(reasonCounts).sort((a, b) => reasonCounts[b] - reasonCounts[a]);

  // Lower is worse. OCR confidence (0-1, only present for scanned pages) is the
  // most granular signal; the model's own high/medium/low fills in when OCR
  // confidence isn't available (text-layer pages, or a flag with no number
  // behind it). Rows with more reasons to distrust them sort lower within a
  // confidence band. Still shown in the Confidence column and used as a
  // same-page tiebreaker, but page order now wins overall - reviewing in
  // document order is easier to follow than jumping around by risk, and
  // within a page this is the best ordering available without per-item
  // position data (see the "click page number to highlight" idea - would
  // need bounding boxes we don't capture yet).
  const EXTRACTION_CONFIDENCE_SCORE: Record<string, number> = { high: 1, medium: 0.6, low: 0.2 };
  const riskScore = (i: InvoiceLineItem): number => {
    const ocr = i.amount_ocr_confidence;
    const model = i.extraction_confidence ? EXTRACTION_CONFIDENCE_SCORE[i.extraction_confidence] : 0.9;
    const base = ocr != null ? Math.min(ocr, model) : model;
    return base - (i.review_reasons?.length || 0) * 0.01;
  };

  const visibleItems = lineItems
    .filter(i => {
      if (filter === 'all') return true;
      if (filter === 'needs_review') return !!i.needs_review;
      if (filter === 'reviewed') return !!i.reviewed;
      return !!i.needs_review && (i.review_reasons || []).includes(filter);
    })
    .slice()
    .sort((a, b) => a.page_number - b.page_number || riskScore(a) - riskScore(b));

  const startEdit = (item: InvoiceLineItem) => {
    setSaveError(null);
    setEditingId(item.line_item_id);
    setDraft({
      vendor: item.vendor || '',
      entry_date: item.entry_date || '',
      description: item.description || '',
      invoice_number: item.invoice_number || '',
      amount: String(item.amount ?? ''),
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft(null);
    setSaveError(null);
  };

  const saveEdit = async (item: InvoiceLineItem) => {
    if (!draft) return;
    const amountNum = parseFloat(draft.amount);
    if (Number.isNaN(amountNum)) {
      setSaveError('Amount must be a number');
      return;
    }
    setSavingId(item.line_item_id);
    setSaveError(null);
    try {
      const updates: InvoiceLineItemUpdate = {
        vendor: draft.vendor,
        entry_date: draft.entry_date,
        description: draft.description,
        invoice_number: draft.invoice_number,
        amount: amountNum,
        mark_reviewed: true,
      };
      const response = await updateInvoiceLineItem(case_id, file_id, item.line_item_id, updates, auth.user?.id_token);
      const updated = normalizeInvoiceLineItem(response.data.line_item);
      setLineItems(prev => prev.map(li => li.line_item_id === item.line_item_id ? { ...li, ...updated } : li));
      setEditingId(null);
      setDraft(null);
    } catch (err) {
      setSaveError('Failed to save changes');
    } finally {
      setSavingId(null);
    }
  };

  const markReviewed = async (item: InvoiceLineItem) => {
    setSavingId(item.line_item_id);
    setSaveError(null);
    try {
      const response = await updateInvoiceLineItem(case_id, file_id, item.line_item_id, { mark_reviewed: true }, auth.user?.id_token);
      const updated = normalizeInvoiceLineItem(response.data.line_item);
      setLineItems(prev => prev.map(li => li.line_item_id === item.line_item_id ? { ...li, ...updated } : li));
    } catch (err) {
      setSaveError('Failed to mark reviewed');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className={multiType ? '' : 'mt-4'}>
      {!multiType && <h5 className="mb-3">Invoice Line Items</h5>}
      {needsReviewCount > 0 && (
        <Alert variant="warning" className="py-2 px-3 mb-2">
          <strong>{needsReviewCount}</strong> of {lineItems.length} entries need review before this data
          should be relied on. Use the filters below to work through them by type; each flagged row says why.
        </Alert>
      )}
      {saveError && <Alert variant="danger" className="py-2 px-3 mb-2">{saveError}</Alert>}
      <div className="d-flex flex-wrap gap-1 mb-2 align-items-center">
        <Button size="sm" variant={filter === 'all' ? 'secondary' : 'outline-secondary'} onClick={() => setFilter('all')}>
          All ({lineItems.length})
        </Button>
        <Button size="sm" variant={filter === 'needs_review' ? 'dark' : 'outline-dark'} onClick={() => setFilter('needs_review')}
          disabled={needsReviewCount === 0}>
          Needs review ({needsReviewCount})
        </Button>
        {reasonKeys.map(r => (
          <Button key={r} size="sm"
            variant={filter === r ? reasonVariant(r) : `outline-${reasonVariant(r)}`}
            onClick={() => setFilter(r)}>
            {reasonLabel(r)} ({reasonCounts[r]})
          </Button>
        ))}
        {reviewedCount > 0 && (
          <Button size="sm" variant={filter === 'reviewed' ? 'success' : 'outline-success'} onClick={() => setFilter('reviewed')}>
            Reviewed ({reviewedCount})
          </Button>
        )}
      </div>
      <div className="d-flex gap-3 mb-2 small text-muted">
        <span>Showing {visibleItems.length} of {lineItems.length} entries</span>
        <span>Total (all entries): {formatCurrency(total)}</span>
      </div>
      {visibleItems.length === 0 && (
        <Alert variant="secondary" className="py-2 px-3">No entries match this filter.</Alert>
      )}
      <div className="table-responsive">
        <table className="table table-sm table-hover mb-0">
          <thead className="table-light">
            <tr>
              <th style={{ width: '52px' }}>Page</th>
              <th style={{ width: '110px' }}>Date</th>
              <th style={{ width: '170px' }}>Vendor</th>
              <th>Description</th>
              <th style={{ width: '100px' }}>Invoice #</th>
              <th className="text-end" style={{ width: '110px' }}>Amount</th>
              <th className="text-end" style={{ width: '90px' }} title="How sure the extraction was, worst-first sorted">Confidence</th>
              <th style={{ width: '300px' }}>Review</th>
              <th style={{ width: '70px' }}></th>
            </tr>
          </thead>
          <tbody>
            {visibleItems.map(item => {
              const isEditing = editingId === item.line_item_id;
              const isSaving = savingId === item.line_item_id;
              if (isEditing && draft) {
                return (
                  <tr key={item.line_item_id} className="table-active">
                    <td className="small">{item.page_number}</td>
                    <td>
                      <Form.Control size="sm" value={draft.entry_date}
                        onChange={e => setDraft({ ...draft, entry_date: e.target.value })} placeholder="YYYY-MM-DD" />
                    </td>
                    <td>
                      <Form.Control size="sm" value={draft.vendor}
                        onChange={e => setDraft({ ...draft, vendor: e.target.value })} />
                    </td>
                    <td>
                      <Form.Control size="sm" value={draft.description}
                        onChange={e => setDraft({ ...draft, description: e.target.value })} />
                    </td>
                    <td>
                      <Form.Control size="sm" value={draft.invoice_number}
                        onChange={e => setDraft({ ...draft, invoice_number: e.target.value })} />
                    </td>
                    <td>
                      <Form.Control size="sm" className="text-end" value={draft.amount}
                        onChange={e => setDraft({ ...draft, amount: e.target.value })} />
                    </td>
                    <td colSpan={3}>
                      <div className="d-flex gap-1">
                        <Button size="sm" variant="primary" disabled={isSaving} onClick={() => saveEdit(item)}>
                          {isSaving ? 'Saving…' : 'Save'}
                        </Button>
                        <Button size="sm" variant="outline-secondary" disabled={isSaving} onClick={cancelEdit}>Cancel</Button>
                      </div>
                    </td>
                  </tr>
                );
              }
              return (
                <tr key={item.line_item_id} className={item.is_duplicate ? 'text-muted' : ''}>
                  <td className="small">
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0"
                      onClick={() => onGoToPage(item.page_number)}
                    >
                      {item.page_number}
                    </Button>
                  </td>
                  <td className="text-nowrap small">{item.entry_date || ''}</td>
                  <td className="small">{item.vendor || ''}</td>
                  <td className="small">{item.description || ''}</td>
                  <td className="small text-muted">{item.invoice_number || ''}</td>
                  <td className="text-end small">{formatCurrency(item.amount)}</td>
                  <td className="text-end small">
                    {item.amount_ocr_confidence != null ? (
                      <span className={item.amount_ocr_confidence < LOW_OCR_CONFIDENCE_THRESHOLD ? 'text-danger fw-semibold' : 'text-muted'}>
                        {Math.round(item.amount_ocr_confidence * 100)}%
                      </span>
                    ) : item.extraction_confidence ? (
                      <span className={item.extraction_confidence === 'low' ? 'text-danger fw-semibold' : 'text-muted'}>
                        {item.extraction_confidence}
                      </span>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  <td className="small">
                    {item.needs_review ? (
                      <div className="d-flex flex-column gap-2">
                        {(item.review_reasons || []).map(reason => {
                          const dup = item.duplicate_of_summary;
                          const isDupReason = reason === 'possible_duplicate' || reason === 'duplicate_same_page';
                          return (
                            <div key={reason}>
                              <span className={`badge bg-${reasonVariant(reason)} ${reasonVariant(reason) === 'warning' ? 'text-dark' : ''} fw-normal`}>
                                {reasonLabel(reason)}
                              </span>
                              <div className="text-muted" style={{ fontSize: '0.75rem', lineHeight: 1.3 }}>
                                {item.review_details?.[reason]}
                                {isDupReason && dup && dup.same_file && !dup.same_page && (
                                  <> <Button variant="link" size="sm" className="p-0 align-baseline" style={{ fontSize: '0.75rem' }}
                                    onClick={() => onGoToPage(dup.page_number)}>Go to page {dup.page_number}</Button></>
                                )}
                              </div>
                            </div>
                          );
                        })}
                        <div>
                          <Button size="sm" variant="outline-success" disabled={isSaving}
                            onClick={() => markReviewed(item)} style={{ fontSize: '0.75rem', padding: '0 6px' }}>
                            {isSaving ? '…' : '✓ Looks fine'}
                          </Button>
                        </div>
                      </div>
                    ) : item.reviewed ? (
                      <span className="badge bg-success fw-normal" title={`Reviewed by ${item.reviewed_by || 'someone'} on ${item.reviewed_at ? formatTimestamp(item.reviewed_at) : ''}`}>
                        ✓ Reviewed
                      </span>
                    ) : null}
                  </td>
                  <td className="small">
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 text-secondary"
                      onClick={() => startEdit(item)}
                      title="Edit this entry"
                    >
                      <PencilSquare size={14} />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── FinancialStatementsPanel ────────────────────────────────────────────────

interface FinancialStatementsPanelProps {
  isFinancialStatementType: boolean;
  statements: FinancialStatement[];
  multiType: boolean;
  processingStatus?: string;
}

const FS_TYPE_LABELS: Record<string, string> = {
  balance_sheet: 'Balance Sheet',
  income_statement: 'Income Statement',
  investment_statement: 'Investment Statement',
  trial_balance: 'Trial Balance',
  other: 'Other',
};

const FS_TOTAL_LABELS: Record<string, string> = {
  total_assets: 'Total Assets',
  total_liabilities: 'Total Liabilities',
  total_capital: 'Total Capital',
  total_revenue: 'Total Revenue',
  total_expenses: 'Total Expenses',
  net_income: 'Net Income',
  total_market_value: 'Total Market Value',
};

const asNumber = (v: number | string | undefined): number => {
  if (v === undefined || v === null || v === '') return 0;
  return typeof v === 'number' ? v : parseFloat(v);
};

const FinancialStatementsPanel: React.FC<FinancialStatementsPanelProps> = ({
  isFinancialStatementType, statements, multiType, processingStatus,
}) => {
  if (!isFinancialStatementType) return null;

  if (processingStatus === 'processing') return null;

  if (processingStatus === 'failed') {
    return (
      <div className={multiType ? '' : 'mt-4'}>
        {!multiType && <h5 className="mb-3">Financial Statements</h5>}
        <Alert variant="danger">Failed to extract statement data from this file.</Alert>
      </div>
    );
  }

  if (statements.length === 0) {
    if (!processingStatus) return null;
    return (
      <div className={multiType ? '' : 'mt-4'}>
        {!multiType && <h5 className="mb-3">Financial Statements</h5>}
        <Alert variant="secondary">No statements were extracted from this file.</Alert>
      </div>
    );
  }

  // Group by entity so a bundle covering multiple years/trusts is easy to scan
  const entityMap = new Map<string, FinancialStatement[]>();
  for (const s of statements) {
    const key = s.entity_name || 'Unknown entity';
    if (!entityMap.has(key)) entityMap.set(key, []);
    entityMap.get(key)!.push(s);
  }
  const entities = Array.from(entityMap.entries()).sort(([a], [b]) => a.localeCompare(b));

  return (
    <div className={multiType ? '' : 'mt-4'}>
      {!multiType && <h5 className="mb-3">Financial Statements</h5>}
      <Accordion alwaysOpen>
        {entities.map(([entityName, entityStatements], entityIdx) => (
          <Accordion.Item key={entityName} eventKey={`entity-${entityIdx}`}>
            <Accordion.Header>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <strong>{entityName}</strong>
                <span className="badge bg-secondary fw-normal">{entityStatements.length} statement(s)</span>
              </div>
            </Accordion.Header>
            <Accordion.Body className="p-0">
              <Accordion alwaysOpen={false} flush>
                {entityStatements
                  .slice()
                  .sort((a, b) => (a.period_end_date || '').localeCompare(b.period_end_date || ''))
                  .map((s, idx) => {
                    const totalsEntries = Object.entries(s.totals || {}).filter(([, v]) => v !== undefined && v !== '');
                    return (
                      <Accordion.Item key={s.statement_id} eventKey={`stmt-${idx}`}>
                        <Accordion.Header>
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            <span className={`badge bg-${
                              s.statement_type === 'balance_sheet' ? 'primary'
                                : s.statement_type === 'income_statement' ? 'success'
                                : s.statement_type === 'investment_statement' ? 'info'
                                : s.statement_type === 'trial_balance' ? 'warning'
                                : 'secondary'
                            } fw-normal`}>
                              {FS_TYPE_LABELS[s.statement_type] ?? s.statement_type}
                            </span>
                            <strong>{s.period_end_date || 'Unknown date'}</strong>
                            {s.institution_name && <span className="text-muted small">{s.institution_name}</span>}
                            {s.account_number && <span className="text-muted small">Acct #{s.account_number}</span>}
                          </div>
                        </Accordion.Header>
                        <Accordion.Body>
                          {totalsEntries.length > 0 && (
                            <div className="d-flex flex-wrap gap-3 mb-3 pb-2 border-bottom">
                              {totalsEntries.map(([key, val]) => (
                                <div key={key}>
                                  <div className="text-muted small">{FS_TOTAL_LABELS[key] ?? key}</div>
                                  <div className="fw-semibold">{formatCurrency(asNumber(val))}</div>
                                </div>
                              ))}
                            </div>
                          )}
                          <div className="table-responsive">
                            <table className="table table-sm table-hover mb-0">
                              <thead className="table-light">
                                <tr>
                                  <th>Line Item</th>
                                  <th className="text-end" style={{ width: '140px' }}>Amount</th>
                                </tr>
                              </thead>
                              <tbody>
                                {s.line_items.map((li, liIdx) => (
                                  <tr key={liIdx}>
                                    <td className="small">{li.label}</td>
                                    <td className="text-end small">{formatCurrency(asNumber(li.amount))}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          {s.original_filename && (
                            <div className="text-muted small mt-2">
                              Source: {s.original_filename}
                              {s.page_numbers && s.page_numbers.length > 0 && ` (page ${s.page_numbers.join(', ')})`}
                            </div>
                          )}
                        </Accordion.Body>
                      </Accordion.Item>
                    );
                  })}
              </Accordion>
            </Accordion.Body>
          </Accordion.Item>
        ))}
      </Accordion>
    </div>
  );
};

// ─── BankStatementsPanel ─────────────────────────────────────────────────────

interface BankStatementsPanelProps {
  fileData: FileData;
  hasBankStatements: boolean;
  isBankStatementType: boolean;
  savingChanges: boolean;
  editingStatementId: string | null;
  setEditingStatementId: (id: string | null) => void;
  handleAddNewStatement: () => void;
  handleSaveStatement: (s: Statement) => void;
  handleDeleteStatement: (id: string) => void;
  getStatementTitle: (s: Statement, i: number) => string;
  setCurrentPage: (page: number) => void;
  case_id: string;
  file_id: string;
  multiType: boolean;
  processingStatus?: string;
}

const BankStatementsPanel: React.FC<BankStatementsPanelProps> = ({
  fileData, hasBankStatements, isBankStatementType, savingChanges,
  editingStatementId, setEditingStatementId,
  handleAddNewStatement, handleSaveStatement, handleDeleteStatement,
  getStatementTitle, setCurrentPage, multiType, processingStatus,
  case_id, file_id,
}) => {
  const auth = useAuth();

  // Entity filter state
  const [entityData, setEntityData] = useState<FileEntityMentionsResponse | null>(null);
  const [entityFilter, setEntityFilter] = useState<string>('all'); // entity_id or 'all' or 'unlinked'

  useEffect(() => {
    if (!hasBankStatements || !case_id || !file_id) return;
    fetchFileEntityMentions(case_id, file_id, auth.user?.id_token)
      .then(setEntityData)
      .catch(() => {/* non-critical — no entity data */});
  }, [hasBankStatements, case_id, file_id, auth.user?.id_token]);

  const entityMap = entityData?.record_map ?? {};
  const entityList: CaseEntity[] = entityData?.entities ?? [];
  const hasEntityFilter = entityList.length > 0;
  if (!hasBankStatements && !isBankStatementType) return null;

  if (!hasBankStatements) {
    // The global "Processing Banner" above the tabs already shows a spinner +
    // message while processingStatus === 'processing' — don't duplicate it here.
    if (processingStatus === 'processing') return null;
    if (processingStatus === 'error') {
      return (
        <Alert variant="danger" className={multiType ? '' : 'mt-3'}>
          <strong>Extraction failed.</strong> Bank statement data could not be extracted automatically.
        </Alert>
      );
    }
    return (
      <Alert variant="warning" className={multiType ? '' : 'mt-3'}>
        This bank statement has not been processed yet. No data has been extracted.
      </Alert>
    );
  }

  return (
    <div className={multiType ? '' : 'mt-3'}>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h5 className="mb-0">Bank Statements</h5>
        <Button
          variant="outline-primary"
          size="sm"
          onClick={handleAddNewStatement}
          className="d-flex align-items-center gap-1"
          disabled={savingChanges}
        >
          <Plus size={16} /> Add Statement
        </Button>
      </div>

      {savingChanges && (
        <Alert variant="info" className="mb-3">Saving changes...</Alert>
      )}

      <Accordion alwaysOpen>
        {[...(fileData.statement_data || [])]
          .sort((a, b) => {
            // Sort by first page number (document order), fall back to date
            const aPage = a.page_numbers?.[0] ?? a.page_number ?? 0;
            const bPage = b.page_numbers?.[0] ?? b.page_number ?? 0;
            if (aPage !== bPage) return aPage - bPage;
            return new Date(a.statement_date).getTime() - new Date(b.statement_date).getTime();
          })
          .map((statement: Statement, index: number) => (
            <Accordion.Item
              key={statement.statement_id}
              eventKey={index.toString()}
              data-statement-id={statement.statement_id}
            >
              <Accordion.Header>
                <div className="d-flex justify-content-between align-items-center w-100 pe-3">
                  <h4 className="mb-0">{getStatementTitle(statement, index)}</h4>
                  <div className="d-flex align-items-center gap-2" onClick={e => e.stopPropagation()}>
                    {(statement.page_numbers?.length || statement.page_number) && (
                      <Button
                        variant="outline-primary"
                        size="sm"
                        style={{ fontSize: '0.75rem', padding: '2px 8px' }}
                        onClick={() => setCurrentPage(statement.page_numbers?.[0] || statement.page_number || 1)}
                      >
                        <i className="bi bi-eye me-1" />
                        {statement.page_numbers?.length
                          ? (statement.page_numbers.length === 1
                              ? `Page ${statement.page_numbers[0]}`
                              : `Pages ${statement.page_numbers[0]}–${statement.page_numbers[statement.page_numbers.length - 1]}`)
                          : `Page ${statement.page_number}`}
                      </Button>
                    )}
                    {editingStatementId !== statement.statement_id && (
                      <Button
                        variant="link"
                        className="p-0 text-primary"
                        onClick={(e) => { e.stopPropagation(); setEditingStatementId(statement.statement_id); }}
                      >
                        <PencilSquare size={16} />
                      </Button>
                    )}
                  </div>
                </div>
              </Accordion.Header>
              <Accordion.Body className="p-0">
                {editingStatementId === statement.statement_id ? (
                  <div className="p-3">
                    <StatementEditor
                      statement={statement}
                      onSave={handleSaveStatement}
                      onCancel={() => setEditingStatementId(null)}
                      onDelete={() => handleDeleteStatement(statement.statement_id)}
                    />
                  </div>
                ) : (
                  <>
                    <div className="p-3">
                      <dl className="row mb-0">
                        <dt className="col-sm-4">Bank</dt>
                        <dd className="col-sm-8">{statement.bank_name}</dd>

                        <dt className="col-sm-4">Account</dt>
                        <dd className="col-sm-8">...{statement.account_number}</dd>

                        <dt className="col-sm-4">Statement Date</dt>
                        <dd className="col-sm-8">{formatDate(statement.statement_date)}</dd>

                        <dt className="col-sm-4">Beginning Balance</dt>
                        <dd className="col-sm-8">{formatCurrency(statement.beginning_balance)}</dd>

                        <dt className="col-sm-4">Ending Balance</dt>
                        <dd className="col-sm-8">{formatCurrency(statement.ending_balance)}</dd>


                      </dl>
                    </div>

                    <div className="border-top">
                      <div className="p-3">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h6 className="mb-0">
                            Transactions
                            {statement.transactions?.length > 0 && (
                              <span className="badge bg-secondary ms-2">{statement.transactions.length}</span>
                            )}
                          </h6>
                          {hasEntityFilter && (
                            <Form.Select
                              size="sm"
                              style={{ width: 'auto', minWidth: '180px' }}
                              value={entityFilter}
                              onChange={e => setEntityFilter(e.target.value)}
                            >
                              <option value="all">All transactions</option>
                              <option value="unlinked">Unlinked</option>
                              {entityList.map(ent => (
                                <option key={ent.entity_id} value={ent.entity_id}>
                                  {ent.name}
                                </option>
                              ))}
                            </Form.Select>
                          )}
                        </div>
                        {statement.transactions && statement.transactions.length > 0 ? (
                          <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                            <table className="table table-sm table-hover mb-0">
                              <thead className="sticky-top bg-white">
                                <tr>
                                  <th>Date</th>
                                  <th>Description</th>
                                  <th>Type</th>
                                  {hasEntityFilter && <th>Entity</th>}
                                  <th className="text-end">Amount</th>
                                </tr>
                              </thead>
                              <tbody>
                                {statement.transactions
                                  .filter((tx: Transaction) => {
                                    if (entityFilter === 'all') return true;
                                    const ref = entityMap[tx.transaction_id];
                                    if (entityFilter === 'unlinked') return !ref;
                                    return ref?.entity_id === entityFilter;
                                  })
                                  .map((tx: Transaction) => {
                                    const ref = entityMap[tx.transaction_id];
                                    const linkedEntity = ref ? entityList.find(e => e.entity_id === ref.entity_id) : null;
                                    return (
                                      <tr key={tx.transaction_id}>
                                        <td>{tx.date}</td>
                                        <td>{tx.description}</td>
                                        <td>
                                          <span className={`badge ${tx.transaction_type === 'credit' ? 'bg-success' : 'bg-danger'}`}>
                                            {tx.transaction_type}
                                          </span>
                                        </td>
                                        {hasEntityFilter && (
                                          <td className="small text-muted">
                                            {linkedEntity ? (
                                              <span title={ref?.role ?? ''}>
                                                {linkedEntity.name}
                                                {ref?.manually_reviewed && (
                                                  <i className="bi bi-check-circle-fill text-success ms-1" title="Manually verified" />
                                                )}
                                              </span>
                                            ) : (
                                              <span className="text-muted fst-italic">—</span>
                                            )}
                                          </td>
                                        )}
                                        <td className={`text-end ${tx.transaction_type === 'credit' ? 'text-success' : 'text-danger'}`}>
                                          {formatCurrency(tx.amount)}
                                        </td>
                                      </tr>
                                    );
                                  })}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <p className="text-muted mb-0">No transactions found for this statement.</p>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </Accordion.Body>
            </Accordion.Item>
          ))}
      </Accordion>
    </div>
  );
};

// ─── CheckImagesPanel ────────────────────────────────────────────────────────

interface CheckImagesPanelProps {
  isCheckImageType: boolean;
  case_id: string;
  file_id: string;
  multiType: boolean;
  onGoToPage: (page: number) => void;
  processingStatus?: string;
}

const CheckImagesPanel: React.FC<CheckImagesPanelProps> = ({
  isCheckImageType, case_id, file_id, multiType, onGoToPage, processingStatus,
}) => {
  if (!isCheckImageType) return null;

  // The global "Processing Banner" above the tabs already shows a spinner +
  // message while processingStatus === 'processing' — don't duplicate it here.
  if (processingStatus === 'processing') return null;

  return (
    <div className={multiType ? '' : 'mt-4'}>
      {!multiType && <h5 className="mb-3">Check Images</h5>}
      <CheckImagesView caseId={case_id} fileId={file_id} onGoToPage={onGoToPage} />
    </div>
  );
};

// ─── CreditCardStatementsPanel ───────────────────────────────────────────────

interface CreditCardStatementsPanelProps {
  isCCType: boolean;
  ccStatements: CCStatement[];
  multiType: boolean;
  processingStatus?: string;
  setCurrentPage: (page: number) => void;
}

const CreditCardStatementsPanel: React.FC<CreditCardStatementsPanelProps> = ({
  isCCType, ccStatements, multiType, processingStatus, setCurrentPage,
}) => {
  if (!isCCType) return null;

  // The global "Processing Banner" above the tabs already shows a spinner +
  // message while processingStatus === 'processing' — don't duplicate it here.
  if (processingStatus === 'processing') return null;

  if (processingStatus === 'error') {
    return (
      <div className={multiType ? '' : 'mt-4'}>
        {!multiType && <h5 className="mb-3">Credit Card Statements</h5>}
        <Alert variant="danger">Failed to extract credit card statement data.</Alert>
      </div>
    );
  }

  if (ccStatements.length === 0) {
    if (!processingStatus) return null;
    return (
      <div className={multiType ? '' : 'mt-4'}>
        {!multiType && <h5 className="mb-3">Credit Card Statements</h5>}
        <Alert variant="secondary">No credit card statements were extracted from this file.</Alert>
      </div>
    );
  }

  const txTypeColor: Record<string, string> = {
    charge: 'danger', payment: 'success', credit: 'success', fee: 'warning',
  };

  return (
    <div className={multiType ? '' : 'mt-4'}>
      {!multiType && <h5 className="mb-3">Credit Card Statements</h5>}
      <Accordion alwaysOpen>
        {[...ccStatements]
          .sort((a, b) => {
            const aPage = a.page_numbers?.[0] ?? a.page_number ?? 0;
            const bPage = b.page_numbers?.[0] ?? b.page_number ?? 0;
            if (aPage !== bPage) return aPage - bPage;
            return new Date(a.statement_date).getTime() - new Date(b.statement_date).getTime();
          })
          .map((stmt, idx) => {
            const date = stmt.statement_date ? new Date(stmt.statement_date) : null;
            const title = date && !isNaN(date.getTime())
              ? date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' }).replace(',', '') + ' Statement'
              : `CC Statement ${idx + 1}`;
            const issuerLabel = [stmt.card_issuer, stmt.card_type].filter(Boolean).join(' ');
            return (
              <Accordion.Item key={stmt.statement_id} eventKey={String(idx)}>
                <Accordion.Header>
                  <div className="d-flex justify-content-between align-items-center w-100 pe-3">
                    <div>
                      <strong>{title}</strong>
                      {issuerLabel && <span className="text-muted ms-2 small">{issuerLabel}</span>}
                    </div>
                    {(stmt.page_numbers?.length || stmt.page_number) && (
                      <Button
                        variant="outline-primary"
                        size="sm"
                        style={{ fontSize: '0.75rem', padding: '2px 8px' }}
                        onClick={e => { e.stopPropagation(); setCurrentPage(stmt.page_numbers?.[0] || stmt.page_number || 1); }}
                      >
                        <i className="bi bi-eye me-1" />
                        {stmt.page_numbers?.length
                          ? (stmt.page_numbers.length === 1
                              ? `Page ${stmt.page_numbers[0]}`
                              : `Pages ${stmt.page_numbers[0]}–${stmt.page_numbers[stmt.page_numbers.length - 1]}`)
                          : `Page ${stmt.page_number}`}
                      </Button>
                    )}
                  </div>
                </Accordion.Header>
                <Accordion.Body className="p-0">
                  <div className="p-3">
                    <dl className="row mb-0">
                      {stmt.card_issuer && <><dt className="col-sm-4">Card Issuer</dt><dd className="col-sm-8">{stmt.card_issuer}</dd></>}
                      {stmt.card_type && <><dt className="col-sm-4">Card Type</dt><dd className="col-sm-8">{stmt.card_type}</dd></>}
                      {stmt.account_number && <><dt className="col-sm-4">Card Number</dt><dd className="col-sm-8">...{stmt.account_number}</dd></>}
                      {stmt.account_owner && <><dt className="col-sm-4">Account Owner</dt><dd className="col-sm-8">{stmt.account_owner}</dd></>}
                      <dt className="col-sm-4">Statement Date</dt>
                      <dd className="col-sm-8">{formatDate(stmt.statement_date)}</dd>
                      {stmt.previous_balance != null && <><dt className="col-sm-4">Previous Balance</dt><dd className="col-sm-8">{formatCurrency(stmt.previous_balance)}</dd></>}
                      {stmt.new_balance != null && <><dt className="col-sm-4">New Balance</dt><dd className="col-sm-8">{formatCurrency(stmt.new_balance)}</dd></>}
                      {stmt.credit_limit != null && <><dt className="col-sm-4">Credit Limit</dt><dd className="col-sm-8">{formatCurrency(stmt.credit_limit)}</dd></>}
                      {stmt.minimum_payment_due != null && <><dt className="col-sm-4">Min. Payment</dt><dd className="col-sm-8">{formatCurrency(stmt.minimum_payment_due)}</dd></>}
                      {stmt.payment_due_date && <><dt className="col-sm-4">Payment Due</dt><dd className="col-sm-8">{formatDate(stmt.payment_due_date)}</dd></>}
                    </dl>
                  </div>
                  <div className="border-top">
                    <div className="p-3">
                      <h6 className="mb-3">
                        Transactions
                        {stmt.transactions?.length > 0 && (
                          <span className="badge bg-secondary ms-2">{stmt.transactions.length}</span>
                        )}
                      </h6>
                      {stmt.transactions && stmt.transactions.length > 0 ? (
                        <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                          <table className="table table-sm table-hover mb-0">
                            <thead className="sticky-top bg-white">
                              <tr>
                                <th>Date</th>
                                <th>Description</th>
                                <th>Type</th>
                                <th className="text-end">Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {stmt.transactions.map((tx: CCTransaction) => (
                                <tr key={tx.transaction_id}>
                                  <td>{tx.date}</td>
                                  <td>{tx.description}</td>
                                  <td>
                                    <span className={`badge bg-${txTypeColor[tx.transaction_type] ?? 'secondary'}`}>
                                      {tx.transaction_type}
                                    </span>
                                  </td>
                                  <td className={`text-end ${tx.transaction_type === 'payment' || tx.transaction_type === 'credit' ? 'text-success' : 'text-danger'}`}>
                                    {formatCurrency(tx.amount)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="text-muted mb-0">No transactions found for this statement.</p>
                      )}
                    </div>
                  </div>
                </Accordion.Body>
              </Accordion.Item>
            );
          })}
      </Accordion>
    </div>
  );
};

// ─── ViewFile ────────────────────────────────────────────────────────────────

const ViewFile: React.FC = () => {
  const { case_id, file_id } = useParams();
  const [fileData, setFileData] = useState<FileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const auth = useAuth();
  const [searchParams] = useSearchParams();
  const initialPage = parseInt(searchParams.get('page') ?? '1', 10) || 1;
  const [numPages, setNumPages] = useState<number | null>(null);
  const [scale, setScale] = useState(1);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [pageInput, setPageInput] = useState(String(initialPage));
  // Manual rotation overrides for the current viewing session, keyed by page
  // number - separate from fileData.file.page_rotations (the saved value) so
  // "Save rotation" has something to compare against and the button only
  // shows up once you've actually changed something.
  const [rotationOverrides, setRotationOverrides] = useState<Record<number, number>>({});
  const [savingRotation, setSavingRotation] = useState(false);
  const [editingStatementId, setEditingStatementId] = useState<string | null>(null);
  const [savingChanges, setSavingChanges] = useState(false);
  const [editingFileType, setEditingFileType] = useState(false);
  const [updatingFileType, setUpdatingFileType] = useState(false);
  const [selectedFileType, setSelectedFileType] = useState<string | null>(null);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const isPollingRefreshRef = useRef(false);
  const viewUrlRef = useRef<string | null>(null);

  useEffect(() => {
    const loadFile = async () => {
      const isPolling = isPollingRefreshRef.current;
      try {
        if (!isPolling) setLoading(true);
        isPollingRefreshRef.current = false;
        if (!isPolling) setPdfError(null);
        const response = await fetchFileContent(case_id!, file_id!, auth.user?.id_token);
        let fileDataWithStatements = response.data;

        // During polling, keep the existing view_url so the PDF viewer doesn't
        // tear down and reload just because the presigned URL was refreshed.
        if (isPolling && viewUrlRef.current) {
          fileDataWithStatements = { ...fileDataWithStatements, view_url: viewUrlRef.current };
        }

        // Determine ALL types present — prefer page_classifications for multi-type files,
        // fall back to the single file_type field.
        const pageClassifications: PageClassification[] = fileDataWithStatements.file?.page_classifications || [];
        const presentTypes = new Set<string>(
          pageClassifications.length > 0
            ? pageClassifications.map((pc: PageClassification) => pc.primary_type)
            : [fileDataWithStatements.file?.file_type || '']
        );

        const hasBankType = ['bank_statement', 'bank-statement', 'bank-statements'].some(t => presentTypes.has(t));
        const hasGlType = ['general_ledger', 'general-ledger'].some(t => presentTypes.has(t));
        const hasCCType = presentTypes.has('credit_card_statement');
        const hasFsType = [
          'financial_statement_other', 'financial_statement_balance_sheet',
          'financial_statement_income', 'investment_account_statement', 'trial_balance',
        ].some(t => presentTypes.has(t));
        const hasInvoiceType = presentTypes.has('invoice');

        // Fetch bank statement data if any page is a bank statement
        if (hasBankType) {
          try {
            const statementsResponse = await fetchProcessedBankStatements(case_id!, auth.user?.id_token);
            const allStatements = statementsResponse.data.statements || [];
            const fileStatements = allStatements.filter((stmt: Statement) => stmt.file_id === file_id);

            const existingStatements = fileDataWithStatements.statement_data || [];
            const existingIds = new Set(existingStatements.map((s: Statement) => s.statement_id));
            const mergedStatements = [...existingStatements];
            for (const stmt of fileStatements) {
              if (!existingIds.has(stmt.statement_id)) {
                mergedStatements.push(stmt);
              }
            }

            if (mergedStatements.length > 0) {
              fileDataWithStatements = { ...fileDataWithStatements, statement_data: mergedStatements };
            }
          } catch (stmtErr) {
            console.debug('No bank statements for this file');
          }
        }

        // Fetch GL entries if any page is a general ledger
        if (hasGlType) {
          try {
            const glResponse = await fetchGlEntries(case_id!, file_id!, auth.user?.id_token);
            const entries: GlEntry[] = glResponse.data.gl_entries || [];
            if (entries.length > 0) {
              fileDataWithStatements = { ...fileDataWithStatements, gl_entries: entries };
            }
          } catch (glErr) {
            console.debug('No GL entries for this file');
          }
        }

        // Fetch extracted financial/investment statements if any page is that type
        if (hasFsType) {
          try {
            const fsResponse = await fetchFinancialStatements(case_id!, file_id!, auth.user?.id_token);
            const fsEntries: FinancialStatement[] = fsResponse.data.financial_statements || [];
            if (fsEntries.length > 0) {
              fileDataWithStatements = { ...fileDataWithStatements, financial_statements: fsEntries };
            }
          } catch (fsErr) {
            console.debug('No financial statements for this file');
          }
        }

        // Fetch invoice line items if any page is an invoice
        if (hasInvoiceType) {
          try {
            const invoiceResponse = await fetchInvoiceLineItems(case_id!, file_id!, auth.user?.id_token);
            const lineItems: InvoiceLineItem[] = invoiceResponse.data.line_items || [];
            if (lineItems.length > 0) {
              fileDataWithStatements = { ...fileDataWithStatements, invoice_line_items: lineItems };
            }
          } catch (invErr) {
            console.debug('No invoice line items for this file');
          }
        }

        // Fetch CC statements if any page is a credit card statement
        if (hasCCType) {
          try {
            const ccResponse = await fetchProcessedCCStatements(case_id!, auth.user?.id_token);
            const allCC: CCStatement[] = ccResponse.data.statements || [];
            const fileCC = allCC.filter((s: CCStatement) => s.file_id === file_id);
            if (fileCC.length > 0) {
              fileDataWithStatements = { ...fileDataWithStatements, cc_statement_data: fileCC };
            }
          } catch (ccErr) {
            console.debug('No CC statements for this file');
          }
        }

        setFileData(fileDataWithStatements);
        // Keep the ref current so polling can re-use the existing presigned URL
        if (fileDataWithStatements.view_url) {
          viewUrlRef.current = fileDataWithStatements.view_url;
        }

        // Initialize the selected tab to the primary file_type
        setSelectedFileType(fileDataWithStatements.file?.file_type || null);
      } catch (err: any) {
        if (err.response?.status === 403) {
          setError('You don\'t have permission to view this file. Please contact the case owner for access.');
        } else {
          setError('Failed to load file');
        }
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    
    if (auth.user?.id_token && case_id && file_id) {
      loadFile();
    }
  }, [case_id, file_id, auth.user?.id_token, refreshCounter]);

  // Polling: re-fetch every 5s while a processor is still running
  const bankProcessing = fileData?.file?.bank_processing_status === 'processing';
  const checkProcessing = fileData?.file?.processing_status === 'processing' || fileData?.file?.status === 'processing';
  const glProcessing = fileData?.file?.gl_processing_status === 'processing';
  const ccProcessing = (fileData?.file as any)?.cc_processing_status === 'processing';
  const invoiceProcessing = (fileData?.file as any)?.invoice_processing_status === 'processing';
  const isAnyProcessing = !!(fileData && (bankProcessing || checkProcessing || glProcessing || ccProcessing || invoiceProcessing));

  useEffect(() => {
    if (!isAnyProcessing) return;
    const timer = setTimeout(() => {
      isPollingRefreshRef.current = true;
      setRefreshCounter(c => c + 1);
    }, 5000);
    return () => clearTimeout(timer);
  }, [isAnyProcessing, refreshCounter]);

  const BackButton = () => (
    <div className="mb-2">
      <Link to={`/case/${case_id}`} className="text-secondary text-decoration-none">
        ← Back to Case
      </Link>
    </div>
  );

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setPdfLoading(false);
    setPdfError(null);
    setCurrentPage(1);
    setPageInput('1');
  };

  const goToPage = (page: number) => {
    const p = isNaN(page) ? 1 : Math.max(1, Math.min(page, numPages || 1));
    setCurrentPage(p);
    setPageInput(String(p));
  };

  const onDocumentLoadError = (error: Error) => {
    console.error('PDF Load Error:', error);
    console.error('PDF URL:', fileData?.view_url);
    console.error('Error details:', {
      message: error.message,
      name: error.name,
      stack: error.stack
    });
    
    // Show detailed error to help debugging
    const errorMsg = error.message || 'Unknown error';
    setPdfError(`Failed to load PDF: ${errorMsg}. Try downloading instead.`);
    setPdfLoading(false);
  };

  const onPageLoadError = (error: Error) => {
    console.error('PDF Page Load Error:', error);
    console.error('Error details:', error.message);
    setPdfError(`Failed to load PDF page: ${error.message}`);
    setPdfLoading(false);
  };

  const handlePdfError = (error: Error) => {
    console.error('PDF Rendering Error:', error);
    console.error('Error details:', {
      message: error.message,
      type: error.name,
      fileUrl: fileData?.view_url
    });
    setPdfError(`PDF rendering failed: ${error.message}. Try downloading the file.`);
    setPdfLoading(false);
  };

  const zoomIn = () => setScale(prev => Math.min(prev + 0.2, 2.5));
  const zoomOut = () => setScale(prev => Math.max(prev - 0.2, 0.2));

  const savedRotation = (page: number) => fileData?.file.page_rotations?.[String(page)] ?? 0;
  const effectiveRotation = (page: number) => rotationOverrides[page] ?? savedRotation(page);
  const rotate = (delta: 90 | -90) => {
    setRotationOverrides(prev => ({
      ...prev,
      [currentPage]: ((effectiveRotation(currentPage) + delta) % 360 + 360) % 360,
    }));
  };
  const rotationDirty = currentPage in rotationOverrides && rotationOverrides[currentPage] !== savedRotation(currentPage);

  const saveRotation = async () => {
    if (!fileData || !case_id || !file_id) return;
    setSavingRotation(true);
    try {
      const page_rotations = { ...(fileData.file.page_rotations || {}), [String(currentPage)]: effectiveRotation(currentPage) };
      await updateFileMetadata(case_id, file_id, { page_rotations }, auth.user?.id_token);
      setFileData(prev => prev ? { ...prev, file: { ...prev.file, page_rotations } } : prev);
      setRotationOverrides(prev => { const next = { ...prev }; delete next[currentPage]; return next; });
    } catch (err) {
      setPdfError('Failed to save rotation - please try again.');
    } finally {
      setSavingRotation(false);
    }
  };

  const resetPdfViewer = () => {
    setPdfError(null);
    setPdfLoading(true);
    setNumPages(null);
    setScale(1);
    setCurrentPage(1);
    setPageInput('1');
  };

  const handleSaveStatement = async (updatedStatement: Statement) => {
    if (!fileData) return;

    try {
      setSavingChanges(true);
      const token = auth.user?.id_token;
      
      // Get current statements
      const currentStatements = fileData.statement_data || [];
      
      // Check if this is a new statement or an update
      const isNewStatement = !currentStatements.find(s => s.statement_id === updatedStatement.statement_id);
      
      let updatedStatements;
      if (isNewStatement) {
        // For new statements, append to the array
        updatedStatements = [...currentStatements, updatedStatement];
      } else {
        // For existing statements, update in place
        updatedStatements = currentStatements.map((stmt: Statement) =>
          stmt.statement_id === updatedStatement.statement_id ? updatedStatement : stmt
        );
      }

      // Save to backend
      await updateStatementData(case_id!, file_id!, updatedStatements, token);

      // Update local state
      setFileData({
        ...fileData,
        statement_data: updatedStatements
      });

      setEditingStatementId(null);
    } catch (err) {
      console.error('Failed to save statement changes:', err);
      setError('Failed to save changes. Please try again.');
    } finally {
      setSavingChanges(false);
    }
  };

  const handleDeleteStatement = async (statementId: string) => {
    if (!fileData) return;

    try {
      setSavingChanges(true);
      const token = auth.user?.id_token;

      // Delete from backend
      await deleteStatement(case_id!, file_id!, statementId, token);

      // Update local state
      setFileData({
        ...fileData,
        statement_data: (fileData.statement_data || []).filter((stmt: Statement) => 
          stmt.statement_id !== statementId
        )
      });

      setEditingStatementId(null);
    } catch (err) {
      console.error('Failed to delete statement:', err);
      setError('Failed to delete statement. Please try again.');
    } finally {
      setSavingChanges(false);
    }
  };

  const handleAddNewStatement = () => {
    if (!fileData) return;

    // Get the last statement's details if available
    const statements = fileData.statement_data || [];
    const lastStatement = statements.length > 0 
      ? statements[statements.length - 1] 
      : null;

    const newStatement: Statement = {
      statement_id: `new-${Date.now()}`,
      bank_name: lastStatement?.bank_name || '',
      account_number: lastStatement?.account_number || '',
      statement_date: '',
      beginning_balance: 0,
      ending_balance: 0,
      file_id: file_id!,
      transactions: []
    };

    setFileData({
      ...fileData,
      statement_data: [...(fileData.statement_data || []), newStatement]
    });

    setEditingStatementId(newStatement.statement_id);
  };

  const handleFileTypeUpdate = async (newFileType: string) => {
    if (!fileData || !case_id || !file_id) return;
    
    try {
      setUpdatingFileType(true);
      const normalizedType = normalizeFileType(newFileType);
      await updateFileMetadata(case_id, file_id, { file_type: normalizedType }, auth.user?.id_token);
      
      // Update local state
      setFileData({
        ...fileData,
        file: {
          ...fileData.file,
          file_type: normalizedType
        }
      });
      
      setEditingFileType(false);
    } catch (error) {
      console.error('Error updating file type:', error);
      alert('Failed to update file type. Please try again.');
    } finally {
      setUpdatingFileType(false);
    }
  };

  const getStatementTitle = (statement: Statement, index: number): string => {
    if (!statement.statement_date) {
      return `Bank Statement ${index + 1} Details`;
    }
    
    const date = new Date(statement.statement_date);
    if (isNaN(date.getTime())) {
      return `Bank Statement ${index + 1} Details`;
    }
    
    // Format: "2022 Apr 28 Statement"
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC'  // Use UTC to avoid timezone issues
    }).replace(',', '') + ' Statement';
  };

  if (loading) return (
    <>
      <BackButton />
      <div>Loading...</div>
    </>
  );

  if (error) return (
    <>
      <BackButton />
      <Alert variant="danger">{error}</Alert>
    </>
  );

  if (!fileData) return (
    <>
      <BackButton />
      <div>No file data found</div>
    </>
  );

  // Check if file has bank statements
  const hasBankStatements = Array.isArray(fileData.statement_data) && fileData.statement_data.length > 0;

  // Build the list of all detected types (from page_classifications, or fall back to file_type)
  const detectedTypes = getDetectedTypes(fileData);
  const isMultiType = (fileData.file.is_multi_type === true) || detectedTypes.length > 1;

  // The currently selected tab (falls back to primary file_type)
  const activeType = selectedFileType ?? fileData.file.file_type;

  // Helpers for the active tab
  const isBankStatementType = activeType === 'bank_statement' || activeType === 'bank-statements' || activeType === 'bank-statement';
  const isCheckImageType = activeType === 'check_image' || activeType === 'check-images' || activeType === 'check-image';
  const isGeneralLedgerType = activeType === 'general_ledger' || activeType === 'general-ledger';
  const isCCType = activeType === 'credit_card_statement';
  const ccStatements = fileData.cc_statement_data || [];
  const ccProcessingStatus = (fileData.file as any).cc_processing_status;
  const FS_TYPES = ['financial_statement_other', 'financial_statement_balance_sheet',
    'financial_statement_income', 'investment_account_statement', 'trial_balance'];
  const isFinancialStatementType = FS_TYPES.includes(activeType);
  const financialStatements = fileData.financial_statements || [];
  const fsProcessingStatus = (fileData.file as any).fs_processing_status;
  const isInvoiceType = activeType === 'invoice';
  const invoiceLineItems = fileData.invoice_line_items || [];
  const invoiceProcessingStatus = (fileData.file as any).invoice_processing_status;

  // Determine actual mime_type (use stored value or fallback to filename detection)
  const actualMimeType = fileData.file?.mime_type && 
                         fileData.file.mime_type !== 'unknown' && 
                         fileData.file.mime_type !== 'application/octet-stream'
    ? fileData.file.mime_type
    : getMimeTypeFromFilename(fileData.file?.filename || '');
  const previewUrl = fileData.preview_url || fileData.view_url;

  return (
    <div>
      <BackButton />
      {fileData.file.status === 'error' && fileData.file.classification_status === 'error' && (
        <Alert variant="danger" className="mb-3">
          <Alert.Heading>Couldn't process this file</Alert.Heading>
          <p className="mb-0">
            {fileData.file.classification_error
              ? `The file could not be classified: ${fileData.file.classification_error}`
              : 'The file could not be classified due to an unexpected error.'}
            {' '}This usually means the file is corrupted, empty, or not actually the format its extension suggests.
            Try re-saving/re-exporting the file and re-uploading it.
          </p>
        </Alert>
      )}
      <Row>
        <Col md={6}>
          {/* Left side - File preview */}
          <div style={{ position: 'sticky', top: '1rem' }}>
            <Card>
              <Card.Header>
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">File Preview</h5>
                  <Button 
                    variant="primary" 
                    size="sm" 
                    onClick={() => window.open(fileData.view_url, '_blank')}
                  >
                    Download
                  </Button>
                </div>
              </Card.Header>
              <Card.Body>
              {!previewUrl ? (
                <Alert variant="warning">
                  <Alert.Heading>No Preview Available</Alert.Heading>
                  <p>Unable to load file preview URL.</p>
                </Alert>
              ) : actualMimeType === 'application/pdf' ? (
                // PDF Viewer
                <div>
                  {/* Navigation toolbar */}
                  <div className="d-flex align-items-center gap-2 mb-2 flex-wrap" style={{ userSelect: 'none' }}>
                    <ButtonGroup size="sm">
                      <Button variant="outline-secondary" onClick={zoomOut} title="Zoom out" style={{ padding: '3px 8px' }}>
                        <ZoomOut size={14} />
                      </Button>
                      <Button variant="outline-secondary" onClick={zoomIn} title="Zoom in" style={{ padding: '3px 8px' }}>
                        <ZoomIn size={14} />
                      </Button>
                    </ButtonGroup>
                    <span className="text-muted small">{Math.round(scale * 100)}%</span>
                    <span style={{ borderLeft: '1px solid #dee2e6', height: '24px', margin: '0 4px' }} />
                    <Button variant="outline-secondary" size="sm" onClick={() => goToPage(1)} disabled={currentPage <= 1} title="First page" style={{ padding: '3px 8px' }}>«</Button>
                    <Button variant="outline-secondary" size="sm" onClick={() => goToPage(currentPage - 1)} disabled={currentPage <= 1} title="Previous page" style={{ padding: '3px 9px' }}>‹</Button>
                    <div className="d-flex align-items-center gap-1">
                      <Form.Control
                        type="number"
                        value={pageInput}
                        min={1}
                        max={numPages || 1}
                        onChange={e => setPageInput(e.target.value)}
                        onBlur={() => goToPage(parseInt(pageInput, 10))}
                        onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter') goToPage(parseInt(pageInput, 10)); }}
                        style={{ width: '54px', textAlign: 'center' }}
                        size="sm"
                      />
                      <span className="text-muted small">/ {numPages ?? '…'}</span>
                    </div>
                    <Button variant="outline-secondary" size="sm" onClick={() => goToPage(currentPage + 1)} disabled={!numPages || currentPage >= numPages} title="Next page" style={{ padding: '3px 9px' }}>›</Button>
                    <Button variant="outline-secondary" size="sm" onClick={() => goToPage(numPages!)} disabled={!numPages || currentPage >= numPages} title="Last page" style={{ padding: '3px 8px' }}>»</Button>
                    <span style={{ borderLeft: '1px solid #dee2e6', height: '24px', margin: '0 4px' }} />
                    <ButtonGroup size="sm">
                      <Button variant="outline-secondary" onClick={() => rotate(-90)} title="Rotate left" style={{ padding: '3px 8px' }}>⟲</Button>
                      <Button variant="outline-secondary" onClick={() => rotate(90)} title="Rotate right" style={{ padding: '3px 8px' }}>⟳</Button>
                    </ButtonGroup>
                    {rotationDirty && (
                      <Button variant="primary" size="sm" onClick={saveRotation} disabled={savingRotation} style={{ padding: '3px 10px' }}>
                        {savingRotation ? 'Saving…' : 'Save rotation'}
                      </Button>
                    )}
                    {!rotationDirty && savedRotation(currentPage) !== 0 && (
                      <span className="text-muted small" title="Auto-detected or previously saved orientation for this page">
                        {savedRotation(currentPage)}° saved
                      </span>
                    )}
                  </div>
                  {/* Single-page PDF display */}
                  <div style={{ height: '560px', overflow: 'auto' }}>
                    <PDFDocument
                      file={fileData.view_url}
                      onLoadSuccess={onDocumentLoadSuccess}
                      loading={
                        <div className="text-center py-5">
                          <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading PDF...</span>
                          </div>
                        </div>
                      }
                      error={
                        <Alert variant="warning">
                          <Alert.Heading>PDF Display Error</Alert.Heading>
                          <p>Could not load PDF file.</p>
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => window.open(fileData.view_url, '_blank')}
                          >
                            Download Instead
                          </Button>
                        </Alert>
                      }
                    >
                      <PDFPage
                        pageNumber={currentPage}
                        scale={scale}
                        rotate={effectiveRotation(currentPage)}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                        onLoadError={onPageLoadError}
                      />
                    </PDFDocument>
                  </div>
                </div>
              ) : actualMimeType.startsWith('image/') ? (
                // Image Viewer
                <div style={{ height: '600px', overflow: 'auto', textAlign: 'center' }}>
                  <img 
                    src={previewUrl} 
                    alt="File preview" 
                    style={{ maxWidth: '100%', height: 'auto' }} 
                  />
                </div>
              ) : (
                // Unknown file type - try to display as is
                <Alert variant="info">
                  <Alert.Heading>Preview Not Available</Alert.Heading>
                  <p>This file type ({actualMimeType}) cannot be previewed in the browser.</p>
                  <Button 
                    variant="primary" 
                    size="sm" 
                    onClick={() => window.open(fileData.view_url, '_blank')}
                  >
                    Download File
                  </Button>
                </Alert>
              )}
            </Card.Body>
          </Card>
          </div>
        </Col>
        
        <Col md={6}>
          {/* Right side - File details */}
          <Card>
            <Card.Header>
              <h5 className="mb-0">File Details</h5>
            </Card.Header>
            <Card.Body>
              <dl className="row mb-0">
                <dt className="col-sm-4">File Name</dt>
                <dd className="col-sm-8">{fileData.file.filename}</dd>
                
                <dt className="col-sm-4">File ID</dt>
                <dd className="col-sm-8">{fileData.file.file_id}</dd>
                
                <dt className="col-sm-4">File Type</dt>
                <dd className="col-sm-8">
                  {editingFileType ? (
                    <Form.Select
                      size="sm"
                      defaultValue={denormalizeFileType(fileData.file.file_type)}
                      onChange={(e) => handleFileTypeUpdate(e.target.value)}
                      disabled={updatingFileType}
                      style={{ maxWidth: '300px' }}
                      autoFocus
                      onBlur={() => !updatingFileType && setEditingFileType(false)}
                    >
                      {FILE_TYPE_OPTIONS.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </Form.Select>
                  ) : (
                    <div className="d-flex align-items-center gap-2">
                      <span>{denormalizeFileType(fileData.file.file_type)}</span>
                      <Button
                        size="sm"
                        variant="link"
                        className="p-0 text-secondary"
                        onClick={() => setEditingFileType(true)}
                        title="Edit file type"
                        style={{ fontSize: '0.8rem' }}
                      >
                        <i className="bi bi-pencil"></i>
                      </Button>
                    </div>
                  )}
                </dd>

                {isMultiType && (
                  <>
                    <dt className="col-sm-4">Also Contains</dt>
                    <dd className="col-sm-8">
                      <div className="d-flex flex-wrap gap-1">
                        {detectedTypes
                          .filter(({ type }) => type !== fileData.file.file_type)
                          .map(({ type, pages }) => (
                            <span key={type} className="badge bg-secondary fw-normal">
                              {denormalizeFileType(type)}
                              {pages.length > 0 && (
                                <span className="ms-1 opacity-75">({formatPageRanges(pages)})</span>
                              )}
                            </span>
                          ))}
                      </div>
                    </dd>
                  </>
                )}

                <dt className="col-sm-4">Upload Date</dt>
                <dd className="col-sm-8">{formatTimestamp(fileData.file.upload_date)}</dd>
                
                <dt className="col-sm-4">Uploaded By</dt>
                <dd className="col-sm-8">{fileData.file.uploaded_by}</dd>
              </dl>
            </Card.Body>
          </Card>

          {/* ── Processing Banner ────────────────────────────── */}
          {isAnyProcessing && (
            <Alert variant="info" className="mt-3 mb-0 py-2 d-flex align-items-center gap-2" style={{ borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }}>
              <div className="spinner-border spinner-border-sm flex-shrink-0" role="status">
                <span className="visually-hidden">Processing…</span>
              </div>
              <span className="small">
                {ccProcessing ? 'Extracting credit card statement data…'
                  : bankProcessing ? 'Extracting bank statement data…'
                  : glProcessing ? 'Extracting general ledger entries…'
                  : invoiceProcessing ? 'Extracting invoice line items…'
                  : 'Processing file…'}
                {' '}This may take a few minutes.
              </span>
            </Alert>
          )}

          {/* ── Content Tabs ─────────────────────────────────────── */}
          {isMultiType ? (
            // Multi-type file: show a tab strip so the user can pick which type to view
            <div className="mt-3">
              <div
                className="d-flex gap-1 flex-wrap"
                style={{ borderBottom: '2px solid #dee2e6', marginBottom: 0 }}
              >
                {detectedTypes.map(({ type, pages }) => {
                  const isActive = activeType === type;
                  const label = denormalizeFileType(type);
                  const pageLabel = pages.length > 0 ? formatPageRanges(pages) : null;
                  return (
                    <button
                      key={type}
                      onClick={() => setSelectedFileType(type)}
                      style={{
                        border: '1px solid',
                        borderBottom: isActive ? '2px solid white' : '1px solid #dee2e6',
                        borderColor: isActive ? '#dee2e6' : '#dee2e6',
                        borderRadius: '4px 4px 0 0',
                        background: isActive ? '#fff' : '#f8f9fa',
                        marginBottom: isActive ? '-2px' : '0',
                        padding: '6px 14px',
                        cursor: 'pointer',
                        fontWeight: isActive ? 600 : 400,
                        color: isActive ? '#0d6efd' : '#495057',
                        fontSize: '0.875rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                      }}
                    >
                      {label}
                      {pageLabel && (
                        <span
                          className="badge"
                          style={{
                            background: isActive ? '#0d6efd' : '#6c757d',
                            color: '#fff',
                            fontWeight: 400,
                            fontSize: '0.7rem',
                          }}
                        >
                          {pageLabel}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Tab panel */}
              <div
                style={{
                  border: '1px solid #dee2e6',
                  borderTop: 'none',
                  borderRadius: '0 0 4px 4px',
                  padding: '1rem',
                }}
              >
                {isBankStatementType && (
                  <BankStatementsPanel
                    fileData={fileData}
                    hasBankStatements={hasBankStatements}
                    isBankStatementType={isBankStatementType}
                    savingChanges={savingChanges}
                    editingStatementId={editingStatementId}
                    setEditingStatementId={setEditingStatementId}
                    handleAddNewStatement={handleAddNewStatement}
                    handleSaveStatement={handleSaveStatement}
                    handleDeleteStatement={handleDeleteStatement}
                    getStatementTitle={getStatementTitle}
                    setCurrentPage={goToPage}
                    case_id={case_id!}
                    file_id={file_id!}
                    multiType={true}
                    processingStatus={fileData.file.bank_processing_status}
                  />
                )}
                {isCheckImageType && (
                  <CheckImagesPanel
                    isCheckImageType={isCheckImageType}
                    case_id={case_id!}
                    file_id={file_id!}
                    multiType={true}
                    onGoToPage={goToPage}
                    processingStatus={fileData.file.processing_status ?? fileData.file.status}
                  />
                )}
                {isGeneralLedgerType && (
                  <GeneralLedgerPanel
                    isGeneralLedgerType={isGeneralLedgerType}
                    glEntries={fileData.gl_entries ?? []}
                    multiType={true}
                    processingStatus={fileData.file.gl_processing_status}
                  />
                )}
                {isCCType && (
                  <CreditCardStatementsPanel
                    isCCType={isCCType}
                    ccStatements={ccStatements}
                    multiType={true}
                    processingStatus={ccProcessingStatus}
                    setCurrentPage={goToPage}
                  />
                )}
                {isFinancialStatementType && (
                  <FinancialStatementsPanel
                    isFinancialStatementType={isFinancialStatementType}
                    statements={financialStatements}
                    multiType={true}
                    processingStatus={fsProcessingStatus}
                  />
                )}
                {isInvoiceType && (
                  <InvoiceLineItemsPanel
                    isInvoiceType={isInvoiceType}
                    lineItems={invoiceLineItems}
                    multiType={true}
                    processingStatus={invoiceProcessingStatus}
                    onGoToPage={goToPage}
                    case_id={case_id!}
                    file_id={file_id!}
                  />
                )}
                {!isBankStatementType && !isCheckImageType && !isGeneralLedgerType && !isCCType && !isFinancialStatementType && !isInvoiceType && (
                  <p className="text-muted mb-0 small">
                    No structured data is extracted for <strong>{denormalizeFileType(activeType)}</strong> files.
                  </p>
                )}
              </div>
            </div>
          ) : (
            // Single-type file: keep the existing flat layout
            <>
              <BankStatementsPanel
                fileData={fileData}
                hasBankStatements={hasBankStatements}
                isBankStatementType={isBankStatementType}
                savingChanges={savingChanges}
                editingStatementId={editingStatementId}
                setEditingStatementId={setEditingStatementId}
                handleAddNewStatement={handleAddNewStatement}
                handleSaveStatement={handleSaveStatement}
                handleDeleteStatement={handleDeleteStatement}
                getStatementTitle={getStatementTitle}
                setCurrentPage={goToPage}
                case_id={case_id!}
                file_id={file_id!}
                multiType={false}
                processingStatus={fileData.file.bank_processing_status}
              />
              <CheckImagesPanel
                isCheckImageType={isCheckImageType}
                case_id={case_id!}
                file_id={file_id!}
                multiType={false}
                onGoToPage={goToPage}
                processingStatus={fileData.file.processing_status ?? fileData.file.status}
              />
              <GeneralLedgerPanel
                isGeneralLedgerType={isGeneralLedgerType}
                glEntries={fileData.gl_entries ?? []}
                multiType={false}
                processingStatus={fileData.file.gl_processing_status}
              />
              <CreditCardStatementsPanel
                isCCType={isCCType}
                ccStatements={ccStatements}
                multiType={false}
                processingStatus={ccProcessingStatus}
                setCurrentPage={goToPage}
              />
              <FinancialStatementsPanel
                isFinancialStatementType={isFinancialStatementType}
                statements={financialStatements}
                multiType={false}
                processingStatus={fsProcessingStatus}
              />
              <InvoiceLineItemsPanel
                isInvoiceType={isInvoiceType}
                lineItems={invoiceLineItems}
                multiType={false}
                processingStatus={invoiceProcessingStatus}
                onGoToPage={goToPage}
                case_id={case_id!}
                file_id={file_id!}
              />
            </>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default ViewFile; 